import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { CAMADA_DA_CHAMADA } from './camadas.ts'
import { createPortal } from 'react-dom'
import { AnchoredOverlay, useToast } from '@orbind/ui'
import { CHAMADA_TOCA_POR_MS, type AvisoChamada, type AvisoDaSala, type SinalDeChamada } from '@orbind/core'
import { canalDePresenca } from '../presence/presence-client.ts'
import { garantirPermissao } from '../app/desktop-permissoes.ts'
import { api } from '../../lib/api.ts'
import { Antessala } from './Antessala.tsx'
import { MenuDeAparelhos, MenuDeFundo } from './controles-de-midia.tsx'
import { Portaria, type PedidoNaPorta } from './Portaria.tsx'
import { VideoDoConvidado } from './ConvidadoNaSala.tsx'
import { MalhaDaSala, type ParticipanteAoVivo } from './sala-mesh.ts'
import { aplicarSaida, guardarAparelho, restricaoDeAudio, restricaoDeVideo } from './dispositivos.ts'
import { aparenciaEstaNeutra, guardarAparencia, type Aparencia } from './aparencia.ts'
import {
  aparenciaInicial,
  aplicarFundoNaCamera,
  estiloDeFundoGuardado,
  guardarEstiloDeFundo,
  guardarImagemDeFundo,
  imagemDeFundoGuardada,
  type EstiloDeFundo,
  type FundoAplicado,
} from './fundo.ts'
import { notificarNoSistema } from './notificacao-nativa.ts'
import './videochamada.css'
import './visual-novo.ts'

/**
 * Chamada da conversa direta — de VÍDEO ou só de VOZ (pedidos 2026-09-14).
 *
 * Mora no SHELL: o telefone precisa tocar em qualquer tela do Orbind.
 *
 * O servidor só repassa sinais (convite, aceitar, recusar, oferta/resposta,
 * ICE, mídia) pelo canal ao vivo que já existe. Imagem e voz vão direto de um
 * navegador ao outro (WebRTC), com STUN público. Sem servidor TURN, redes muito
 * fechadas podem não conectar — a chamada avisa em vez de ficar muda.
 *
 * A conexão tem DOIS canais de vídeo (SPEC-20260917-0004): o primeiro leva a
 * câmera, o segundo a tela compartilhada. Ambos nascem com a chamada, mesmo
 * numa chamada de voz, então ligar a câmera ou compartilhar a tela é só trocar
 * o que vai neles (`replaceTrack`), sem renegociar — nada de "reconectando" no
 * meio da conversa. Com os dois canais, quem recebe vê a tela em destaque E o
 * rosto de quem apresenta ao mesmo tempo, como no Meet.
 *
 * Compartilhar tela COM SOM (pedido 2026-09-14) funciona do mesmo jeito: o
 * áudio da tela entra no MESMO canal de voz do microfone — o navegador só dá
 * um canal de áudio por conexão — MISTURADOS com Web Audio (`faixaDeAudioParaEnviar`),
 * para a sua voz continuar saindo junto com o som do que você compartilha. A
 * ESCOLHA de incluir o som é do PRÓPRIO seletor de tela do navegador (o
 * interruptor "Compartilhar áudio do sistema" dele) — pedimos `audio: true`
 * sempre para esse interruptor aparecer; um menu nosso perguntando a mesma
 * coisa antes disso virou pergunta em dobro (2ª correção, 2026-09-14) e saiu.
 * Depois de compartilhando, dá para silenciar só o som da tela sem parar de
 * compartilhar a imagem — esse controle é nosso mesmo, fica nos botões da
 * chamada.
 *
 * Volume de quem EU OUÇO (outro pedido 2026-09-14) é só PLAYBACK local — o
 * `<video>` remoto tem um controle de volume próprio, sem sinal nenhum para o
 * outro lado.
 *
 * A barra "Você está compartilhando sua tela" que o Windows/Chrome desenha por
 * cima de tudo é um aviso de segurança do PRÓPRIO SISTEMA — nem um site nem um
 * app que só embrulha o Chromium (como este) tem como escondê-la; existe
 * bem por isto: para ninguém compartilhar a tela da pessoa sem ela perceber.
 */

/** Identifica ESTA janela: a mesma pessoa pode ter várias abertas. */
const SESSAO = novoId()

const SERVIDORES_ICE: RTCIceServer[] = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }]

/** Atendeu mas a conexão não fechou neste prazo: desiste com aviso. */
const PRAZO_PARA_CONECTAR_MS = 30_000

/**
 * `antessala` (SPEC-20260917-0047) é a preparação ANTES de discar: existe só
 * aqui, nada foi enviado ao outro lado ainda — o telefone de ninguém toca.
 */
type Fase = 'antessala' | 'chamando' | 'tocando' | 'conectando' | 'em-chamada'

type Chamada = {
  fase: Fase
  callId: string
  channelId: string
  outro: { memberId: string; nome: string; foto: string | null }
  /** Quem liga cria a oferta; quem atende responde. */
  papel: 'liga' | 'atende'
  /** Como a chamada COMEÇOU (o convite): de vídeo ou de voz. */
  video: boolean
}

type ContextoDaChamada = {
  /** Há chamada em andamento (tocando, chamando ou conectada)? */
  ocupado: boolean
  ligar: (
    outro: { memberId: string; nome: string; foto: string | null },
    channelId: string,
    opcoes: { video: boolean },
  ) => void
}

const Contexto = createContext<ContextoDaChamada | null>(null)

export function useVideochamada(): ContextoDaChamada {
  const valor = useContext(Contexto)
  if (!valor) throw new Error('useVideochamada fora do VideochamadaProvider')
  return valor
}

export function VideochamadaProvider({ children }: { children: ReactNode }) {
  const toast = useToast()
  const [chamada, setChamada] = useState<Chamada | null>(null)
  const chamadaRef = useRef<Chamada | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const icePendente = useRef<RTCIceCandidateInit[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pararSom = useRef<(() => void) | null>(null)

  // Mídia local em peças separadas: o microfone vive a chamada toda; câmera e
  // tela entram e saem.
  const microfoneRef = useRef<MediaStreamTrack | null>(null)
  const cameraRef = useRef<MediaStreamTrack | null>(null)
  const telaRef = useRef<MediaStreamTrack | null>(null)
  /** O áudio da TELA (som do computador), quando a pessoa escolhe "com som". */
  const telaAudioRef = useRef<MediaStreamTrack | null>(null)
  /** Mistura microfone + som da tela num só fluxo — é o que vai no canal de voz. */
  const mixerRef = useRef<{
    ctx: AudioContext
    fonteMic: MediaStreamAudioSourceNode | null
    fonteTela: MediaStreamAudioSourceNode
  } | null>(null)
  /**
   * FUNDO da câmera (SPEC-20260916-2346): quando ligado, o que vai para a
   * chamada é a câmera JÁ processada (pessoa recortada, fundo borrado ou com a
   * imagem). Falhou em ligar? A câmera segue direta.
   *
   * A APARÊNCIA (SPEC-20260918-0040) usa o mesmo processamento — e sozinha, com
   * o fundo desligado, ele roda sem modelo nenhum. Com fundo desligado E
   * aparência neutra não há processamento: a câmera vai direta.
   */
  const fundoRef = useRef<FundoAplicado | null>(null)
  /** O que chega do outro lado: voz + câmera dele. */
  const remotoRef = useRef<MediaStream>(new MediaStream())
  /**
   * A TELA que o outro lado compartilha, em canal PRÓPRIO (SPEC-20260917-0004):
   * com dois canais de vídeo dá para ver a tela E a cara da pessoa ao mesmo
   * tempo, como no Meet. Com um canal só, um substituía o outro.
   */
  const remotoTelaRef = useRef<MediaStream>(new MediaStream())

  const [local, setLocal] = useState<MediaStream | null>(null)
  const [localTela, setLocalTela] = useState<MediaStream | null>(null)
  const [remoto, setRemoto] = useState<MediaStream | null>(null)
  const [remotoTela, setRemotoTela] = useState<MediaStream | null>(null)
  const [micLigado, setMicLigado] = useState(true)
  const [camLigada, setCamLigada] = useState(false)
  const [compartilhando, setCompartilhando] = useState(false)
  /** A tela compartilhada trouxe áudio (nem toda fonte deixa)? */
  const [telaComAudio, setTelaComAudio] = useState(false)
  /** O som da tela está indo na chamada agora (dá para silenciar sem parar de compartilhar)? */
  const [somDaTela, setSomDaTela] = useState(true)
  const [outroMidia, setOutroMidia] = useState({ video: false, tela: false })
  const [inicio, setInicio] = useState<number | null>(null)
  const [estiloFundo, setEstiloFundo] = useState<EstiloDeFundo>(() => estiloDeFundoGuardado())
  const [imagemFundo, setImagemFundo] = useState<string | null>(() => imagemDeFundoGuardada())
  /** Suavizar, clarear e nitidez (SPEC-20260918-0040) — a escolha é do computador. */
  const [aparencia, setAparencia] = useState<Aparencia>(() => aparenciaInicial())

  /* ------------------------------ sala com portaria (SPEC-20260917-0047) --- */

  /** A sala desta chamada: existe desde a antessala, porque o link nasce lá. */
  const [sala, setSala] = useState<{ roomId: string; inviteToken: string } | null>(null)
  const salaRef = useRef<{ roomId: string; inviteToken: string } | null>(null)
  /** Quem bateu na porta e ainda espera decisão — só o dono vê. */
  const [naPorta, setNaPorta] = useState<PedidoNaPorta[]>([])
  /** Quem entrou pelo link e já está na sala. */
  const [convidados, setConvidados] = useState<ParticipanteAoVivo[]>([])
  const malhaRef = useRef<MalhaDaSala | null>(null)

  const guardarSala = (nova: { roomId: string; inviteToken: string } | null) => {
    salaRef.current = nova
    setSala(nova)
  }

  const atualizar = (nova: Chamada | null) => {
    chamadaRef.current = nova
    setChamada(nova)
  }

  const enviar = (c: Pick<Chamada, 'callId' | 'channelId' | 'outro'>, sinal: SinalDeChamada) =>
    canalDePresenca.enviarSinalDeChamada({
      callId: c.callId,
      channelId: c.channelId,
      para: c.outro.memberId,
      sessao: SESSAO,
      sinal,
    })

  /**
   * Prévias locais separadas (SPEC-20260917-0004): a minha câmera numa, a
   * minha tela compartilhada em outra — assim as duas aparecem juntas, como
   * acontece do lado de quem recebe.
   */
  const atualizarPrevia = () => {
    const faixas = [microfoneRef.current, faixaDeVideoParaEnviar()].filter((t): t is MediaStreamTrack => t !== null)
    setLocal(faixas.length > 0 ? new MediaStream(faixas) : null)
    setLocalTela(telaRef.current ? new MediaStream([telaRef.current]) : null)
  }

  /** A imagem que sai daqui: a câmera com fundo, ou a câmera direta. */
  const faixaDeVideoParaEnviar = (): MediaStreamTrack | null => fundoRef.current?.faixa ?? cameraRef.current

  const pararFundo = () => {
    fundoRef.current?.parar()
    fundoRef.current = null
  }

  const avisarMidia = () => {
    const atual = chamadaRef.current
    // Na antessala não há com quem falar ainda; tocando, o outro lado é quem liga.
    if (!atual || atual.fase === 'tocando' || atual.fase === 'antessala') return
    enviar(atual, { acao: 'midia', video: cameraRef.current !== null, tela: telaRef.current !== null })
    malhaRef.current?.anunciarMidia(cameraRef.current !== null, telaRef.current !== null)
  }

  const silenciar = () => {
    pararSom.current?.()
    pararSom.current = null
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }

  /** Desmonta a mistura de áudio (mic + som da tela) sem parar as faixas. */
  const pararMixer = () => {
    const m = mixerRef.current
    if (!m) return
    try {
      m.fonteMic?.disconnect()
      m.fonteTela.disconnect()
      void m.ctx.close()
    } catch {
      // já fechado, ou nunca chegou a abrir — sem problema
    }
    mixerRef.current = null
  }

  /** Desfaz TUDO: som, prazo, conexão, câmera, tela e microfone. */
  const limpar = useCallback(
    (aviso?: string) => {
      silenciar()
      pcRef.current?.close()
      pcRef.current = null
      pararFundo()
      for (const ref of [microfoneRef, cameraRef, telaRef, telaAudioRef]) {
        ref.current?.stop()
        ref.current = null
      }
      pararMixer()
      remotoRef.current = new MediaStream()
      remotoTelaRef.current = new MediaStream()
      icePendente.current = []
      setLocal(null)
      setLocalTela(null)
      setRemoto(null)
      setRemotoTela(null)
      setInicio(null)
      setMicLigado(true)
      setCamLigada(false)
      setCompartilhando(false)
      setTelaComAudio(false)
      setSomDaTela(true)
      setOutroMidia({ video: false, tela: false })
      encerrarSala(chamadaRef.current?.papel === 'liga')
      atualizar(null)
      if (aviso) toast.push({ tone: 'info', message: aviso })
    },
    [toast],
  )

  /** Microfone sempre; câmera só se pedida. Sem microfone, desiste. */
  const obterMidia = async (comCamera: boolean): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.push({ tone: 'danger', message: 'Este aparelho não permite chamadas.' })
      return false
    }
    // No desktop quem pergunta é o Orbind, não a janela do motor
    // (SPEC-20260916-2335): sem o "sim" aqui, nem chega a pedir a mídia.
    if (!(await garantirPermissao('microfone'))) {
      toast.push({ tone: 'warning', message: 'Sem o microfone não dá para falar na chamada. Libere em Preferências.' })
      return false
    }
    if (comCamera) await garantirPermissao('camera')
    try {
      // já com o microfone e a câmera escolhidos em Preferências (SPEC-20260916-2346)
      const midia = await navigator.mediaDevices.getUserMedia({
        audio: restricaoDeAudio(),
        video: comCamera ? restricaoDeVideo() : false,
      })
      microfoneRef.current = midia.getAudioTracks()[0] ?? null
      cameraRef.current = midia.getVideoTracks()[0] ?? null
    } catch {
      if (!comCamera) {
        toast.push({ tone: 'danger', message: 'Sem acesso ao microfone. Libere a permissão e tente de novo.' })
        return false
      }
      // Câmera negada ou ocupada: segue só com voz.
      try {
        const soVoz = await navigator.mediaDevices.getUserMedia({ audio: restricaoDeAudio() })
        microfoneRef.current = soVoz.getAudioTracks()[0] ?? null
        cameraRef.current = null
        toast.push({ tone: 'info', message: 'Câmera indisponível — a chamada segue só com voz.' })
      } catch {
        toast.push({
          tone: 'danger',
          message: 'Sem acesso à câmera e ao microfone. Libere a permissão no navegador e tente de novo.',
        })
        return false
      }
    }
    setCamLigada(cameraRef.current !== null)
    atualizarPrevia()
    return true
  }

  /**
   * A faixa que vai no canal de voz: só o microfone, ou o microfone MISTURADO
   * com o som da tela compartilhada. Só existe UM canal de áudio na conexão —
   * é por isso que precisa misturar em vez de mandar os dois separados.
   */
  const faixaDeAudioParaEnviar = (): MediaStreamTrack | null => {
    const mic = microfoneRef.current
    const tela = telaAudioRef.current
    pararMixer()
    if (!tela) return mic
    try {
      const ctx = new AudioContext()
      // Sem isto o contexto nasce SUSPENSO (política de autoplay do navegador,
      // já que a essa altura passou um `await` desde o clique) e a mistura
      // fica muda — o áudio da tela não saía de jeito nenhum (relato do
      // usuário, 2026-09-14).
      void ctx.resume().catch(() => undefined)
      const destino = ctx.createMediaStreamDestination()
      const fonteMic = mic ? ctx.createMediaStreamSource(new MediaStream([mic])) : null
      const fonteTela = ctx.createMediaStreamSource(new MediaStream([tela]))
      fonteMic?.connect(destino)
      fonteTela.connect(destino)
      mixerRef.current = { ctx, fonteMic, fonteTela }
      return destino.stream.getAudioTracks()[0] ?? mic
    } catch {
      // Web Audio falhou (raro): sem mistura, mas a chamada continua com voz.
      return mic
    }
  }

  /**
   * Os canais de vídeo da conexão, na ordem do SDP: o PRIMEIRO é sempre a
   * câmera, o SEGUNDO é a tela compartilhada (SPEC-20260917-0004). A ordem é a
   * mesma dos dois lados porque quem liga cria os canais nessa sequência e quem
   * atende os recebe pela oferta.
   */
  const canaisDeVideo = (pc: RTCPeerConnection) =>
    pc.getTransceivers().filter((t) => t.receiver.track?.kind === 'video')

  /** Coloca microfone, câmera e tela atuais nos canais da conexão. */
  const ligarFaixasNaConexao = async (pc: RTCPeerConnection) => {
    for (const transceptor of pc.getTransceivers()) {
      if (transceptor.receiver.track?.kind !== 'audio') continue
      transceptor.direction = 'sendrecv'
      await transceptor.sender.replaceTrack(faixaDeAudioParaEnviar()).catch(() => undefined)
    }
    const videos = canaisDeVideo(pc)
    const faixas = [faixaDeVideoParaEnviar(), telaRef.current]
    for (const [i, transceptor] of videos.entries()) {
      transceptor.direction = 'sendrecv'
      await transceptor.sender.replaceTrack(faixas[i] ?? null).catch(() => undefined)
    }
  }

  const criarConexao = (c: Chamada): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: SERVIDORES_ICE })
    // Quem liga abre SEMPRE os dois canais (voz e vídeo), mesmo numa chamada de
    // voz: é o que deixa ligar a câmera ou a tela depois sem renegociar. Quem
    // atende recebe os canais pela oferta e só preenche.
    if (c.papel === 'liga') {
      pc.addTransceiver('audio', { direction: 'sendrecv' })
      // DOIS canais de vídeo: câmera e tela (SPEC-20260917-0004). Criados desde
      // o começo, ligar a câmera ou compartilhar a tela depois não renegocia.
      pc.addTransceiver('video', { direction: 'sendrecv' })
      pc.addTransceiver('video', { direction: 'sendrecv' })
      void ligarFaixasNaConexao(pc)
    }
    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return
      enviar(c, {
        acao: 'ice',
        candidato: {
          candidate: ev.candidate.candidate,
          sdpMid: ev.candidate.sdpMid,
          sdpMLineIndex: ev.candidate.sdpMLineIndex,
        },
      })
    }
    /**
     * Dois destinos: voz + câmera num fluxo, a tela compartilhada em outro
     * (SPEC-20260917-0004). Quem diz qual é qual é a POSIÇÃO do canal de vídeo
     * (o segundo é sempre a tela), e não o conteúdo — a faixa é igual nos dois.
     */
    pc.ontrack = (ev) => {
      const ehTela = ev.track.kind === 'video' && canaisDeVideo(pc).indexOf(ev.transceiver) === 1
      if (ehTela) {
        remotoTelaRef.current.addTrack(ev.track)
        setRemotoTela(new MediaStream(remotoTelaRef.current.getTracks()))
        return
      }
      remotoRef.current.addTrack(ev.track)
      setRemoto(new MediaStream(remotoRef.current.getTracks()))
    }
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return
      if (pc.connectionState === 'connected') {
        silenciar()
        const atual = chamadaRef.current
        if (atual && atual.fase !== 'em-chamada') {
          atualizar({ ...atual, fase: 'em-chamada' })
          setInicio(Date.now())
          avisarMidia()
          // Conectou: agora a sala pode receber gente de fora. Quem ligou avisa
          // o outro lado do id, senão só um dos dois enxergaria o convidado.
          const minhaSala = salaRef.current
          if (minhaSala && atual.papel === 'liga') enviar(atual, { acao: 'sala', roomId: minhaSala.roomId })
        }
      }
      if (pc.connectionState === 'failed') {
        enviar(c, { acao: 'encerrar' })
        limpar('Não foi possível conectar a chamada. A rede de um dos lados pode estar bloqueando.')
      }
    }
    pcRef.current = pc
    return pc
  }

  const aplicarIcePendente = async (pc: RTCPeerConnection) => {
    for (const candidato of icePendente.current.splice(0)) {
      await pc.addIceCandidate(candidato).catch(() => undefined)
    }
  }

  const prazoParaConectar = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const atual = chamadaRef.current
      if (atual && atual.fase === 'conectando') {
        enviar(atual, { acao: 'encerrar' })
        limpar('Não foi possível conectar a chamada.')
      }
    }, PRAZO_PARA_CONECTAR_MS)
  }

  /**
   * Troca o que sai em CADA canal de vídeo, sem renegociar: câmera no primeiro,
   * tela no segundo (SPEC-20260917-0004). Um não apaga mais o outro.
   */
  const trocarVideoNaConexao = async () => {
    const pc = pcRef.current
    if (pc) {
      const videos = canaisDeVideo(pc)
      const faixas = [faixaDeVideoParaEnviar(), telaRef.current]
      for (const [i, t] of videos.entries()) {
        await t.sender.replaceTrack(faixas[i] ?? null).catch(() => undefined)
      }
    }
    // Quem está na sala pelo link vê a mesma troca, pelas conexões da malha.
    await malhaRef.current?.atualizarMidia()
    atualizarPrevia()
    avisarMidia()
  }

  /** Troca o áudio que sai (mic, ou mic + som da tela) sem renegociar. */
  const trocarAudioNaConexao = async () => {
    const pc = pcRef.current
    const transceptor = pc?.getTransceivers().find((t) => t.receiver.track.kind === 'audio')
    if (transceptor) await transceptor.sender.replaceTrack(faixaDeAudioParaEnviar()).catch(() => undefined)
    await malhaRef.current?.atualizarMidia()
  }

  /* ----------------------------------------------- ligar e atender --- */

  /**
   * O botão de chamar abre a ANTESSALA (SPEC-20260917-0047), não a chamada: a
   * mídia local sobe e a pessoa se prepara. Discar é o passo seguinte, no
   * `chamarAgora`. Enquanto isto, nada foi enviado ao outro lado.
   */
  const ligar = useCallback<ContextoDaChamada['ligar']>(
    (outro, channelId, opcoes) => {
      if (chamadaRef.current) {
        toast.push({ tone: 'info', message: 'Você já está em uma chamada.' })
        return
      }
      void (async () => {
        if (!(await obterMidia(opcoes.video))) return
        // O fundo escolhido vale já na prévia: é o que a pessoa veria do outro lado.
        await ligarFundoSePreciso()
        atualizarPrevia()
        atualizar({
          fase: 'antessala',
          callId: novoId(),
          channelId,
          outro,
          papel: 'liga',
          video: opcoes.video,
        })
        // A sala nasce junto com a antessala: é dela que sai o link do convite,
        // e o link precisa estar pronto ANTES de chamar — quem convida alguém
        // de fora manda o link primeiro e disca depois.
        try {
          const resposta = await api.calls.rooms.$post({ json: { channelId } })
          if (resposta.ok) {
            const nova = await resposta.json()
            guardarSala(nova)
            // Entrar já na antessala: o link costuma ser enviado ANTES de
            // discar, e quem clica nele não pode bater numa porta sem ninguém
            // atrás. Entrar não conecta nada sozinho — a malha só abre conexão
            // quando um convidado aparece.
            entrarNaSalaDaChamada(nova.roomId)
          }
        } catch {
          // Sem sala dá para chamar do mesmo jeito; só não dá para convidar de fora.
        }
      })()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limpar, toast],
  )

  /**
   * A malha da sala, criada sob demanda.
   *
   * Só nasce quando a sala entra em jogo (alguém pelo link); a chamada de duas
   * pessoas não passa por aqui.
   */
  const malha = (): MalhaDaSala => {
    if (malhaRef.current) return malhaRef.current
    const nova = new MalhaDaSala({
      enviar: (para, sinal) => canalDePresenca.enviarSinalDaSala(para, sinal),
      aoMudar: (participantes) => setConvidados(participantes),
      fontes: {
        audio: () => faixaDeAudioParaEnviar(),
        video: () => faixaDeVideoParaEnviar(),
        tela: () => telaRef.current,
      },
    })
    malhaRef.current = nova
    return nova
  }

  /**
   * Entra na sala desta chamada para poder receber quem vem pelo link.
   *
   * Entrar na sala NÃO conecta os dois membros de novo: a malha só abre
   * conexão quando um dos lados é convidado (ver sala-mesh).
   */
  const entrarNaSalaDaChamada = (roomId: string) => {
    malha()
    canalDePresenca.entrarNaSala(roomId)
  }

  const encerrarSala = (souDono: boolean) => {
    malhaRef.current?.encerrar()
    malhaRef.current = null
    canalDePresenca.sairDaSala()
    setConvidados([])
    setNaPorta([])
    const atual = salaRef.current
    guardarSala(null)
    // Fechar a sala mata o link: ninguém bate numa porta que não existe mais.
    if (souDono && atual) {
      void api.calls.rooms[':id'].close.$post({ param: { id: atual.roomId } }).catch(() => undefined)
    }
  }

  /** Deixa entrar ou barra quem está na porta. */
  const decidirNaPorta = (guestId: string, aprovar: boolean) => {
    canalDePresenca.decidirNaPortaria(guestId, aprovar)
    setNaPorta((atual) => atual.filter((p) => p.guestId !== guestId))
  }

  /** "Chamar agora" da antessala: agora sim o telefone do outro lado toca. */
  const chamarAgora = () => {
    const atual = chamadaRef.current
    if (!atual || atual.fase !== 'antessala') return
    const chamando: Chamada = { ...atual, fase: 'chamando' }
    atualizar(chamando)
    if (!enviar(chamando, { acao: 'convite', video: atual.video })) {
      limpar('Sem conexão com o servidor. Tente de novo em instantes.')
      return
    }
    pararSom.current = tocarSom('chamando')
    timer.current = setTimeout(() => {
      const agora = chamadaRef.current
      if (agora?.callId === chamando.callId && agora.fase === 'chamando') {
        enviar(chamando, { acao: 'cancelar' })
        limpar(`${atual.outro.nome} não atendeu.`)
      }
    }, CHAMADA_TOCA_POR_MS)
  }

  /** Atender: `comCamera` decide se já entra mostrando o rosto. */
  const aceitar = async (comCamera: boolean) => {
    const atual = chamadaRef.current
    if (!atual || atual.fase !== 'tocando') return
    silenciar()
    if (!(await obterMidia(comCamera))) {
      enviar(atual, { acao: 'recusar' })
      limpar()
      return
    }
    // Enquanto pedia permissão, quem ligou pode ter desistido.
    if (chamadaRef.current?.callId !== atual.callId) {
      microfoneRef.current?.stop()
      cameraRef.current?.stop()
      return
    }
    const conectando: Chamada = { ...atual, fase: 'conectando' }
    atualizar(conectando)
    // A conexão nasce ANTES do "aceitar": a oferta pode chegar logo atrás.
    criarConexao(conectando)
    enviar(conectando, { acao: 'aceitar' })
    prazoParaConectar()
  }

  const recusar = () => {
    const atual = chamadaRef.current
    if (!atual) return
    enviar(atual, { acao: 'recusar' })
    limpar()
  }

  const desligar = () => {
    const atual = chamadaRef.current
    if (!atual) return
    // Desistir na antessala não avisa ninguém: não havia chamada nenhuma.
    if (atual.fase !== 'antessala') {
      enviar(atual, { acao: atual.fase === 'chamando' ? 'cancelar' : 'encerrar' })
    }
    limpar()
  }

  const alternarMic = () => {
    const ligado = !micLigado
    if (microfoneRef.current) microfoneRef.current.enabled = ligado
    setMicLigado(ligado)
  }

  /**
   * TROCAR DE APARELHO no meio da chamada (SPEC-20260916-2346): pega a mídia do
   * aparelho novo, troca a faixa que já está na conexão (sem renegociar) e
   * descarta a antiga. Falhou (aparelho sumiu, ocupado): segue com o de antes.
   */
  const trocarMicrofone = async (id: string) => {
    guardarAparelho('microfone', id)
    try {
      const midia = await navigator.mediaDevices.getUserMedia({ audio: restricaoDeAudio() })
      const nova = midia.getAudioTracks()[0] ?? null
      if (!nova) return
      nova.enabled = micLigado
      microfoneRef.current?.stop()
      microfoneRef.current = nova
      await trocarAudioNaConexao()
      atualizarPrevia()
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível usar esse microfone.' })
    }
  }

  const trocarCamera = async (id: string) => {
    guardarAparelho('camera', id)
    if (!cameraRef.current) return // câmera desligada: vale na próxima vez que ligar
    try {
      const midia = await navigator.mediaDevices.getUserMedia({ video: restricaoDeVideo() })
      const nova = midia.getVideoTracks()[0] ?? null
      if (!nova) return
      // o fundo estava ligado na câmera antiga: refaz na nova
      pararFundo()
      cameraRef.current.stop()
      cameraRef.current = nova
      await ligarFundoSePreciso()
      await trocarVideoNaConexao()
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível usar essa câmera.' })
    }
  }

  /**
   * Põe o processamento da câmera no estado pedido — fundo (SPEC-20260916-2346)
   * e aparência (SPEC-20260918-0040) saem do mesmo canvas.
   *
   * Nada escolhido: não há processamento nenhum, a câmera vai direta. Já
   * ligado: ajusta ao vivo, sem recriar (a imagem não pisca). O `ajustar`
   * devolve `false` num caso só — quando este processamento nasceu sem o modelo
   * de recorte, por ser aparência com fundo desligado, e agora pedem fundo; aí
   * não tem jeito, recria.
   */
  const sincronizarProcessamentoDaCamera = async (
    estilo: EstiloDeFundo,
    imagem: string | null,
    ajustesDeAparencia: Aparencia,
  ) => {
    if (!cameraRef.current || (estilo === 'nenhum' && aparenciaEstaNeutra(ajustesDeAparencia))) {
      pararFundo()
      return
    }
    if (fundoRef.current?.ajustar(estilo, imagem, ajustesDeAparencia)) return
    pararFundo()
    const aplicado = await aplicarFundoNaCamera(cameraRef.current, estilo, imagem, ajustesDeAparencia)
    if (!aplicado) {
      toast.push({
        tone: 'warning',
        message:
          estilo === 'nenhum'
            ? 'Não foi possível aplicar os ajustes de aparência aqui — a câmera segue sem eles.'
            : 'Não foi possível preparar o fundo aqui — a câmera segue sem ele.',
      })
      return
    }
    fundoRef.current = aplicado
  }

  /** Liga fundo e aparência na câmera atual, se houver o que aplicar. */
  const ligarFundoSePreciso = () => sincronizarProcessamentoDaCamera(estiloFundo, imagemFundo, aparencia)

  /** Troca o fundo no meio da chamada (nenhum, desfoque ou imagem). */
  const trocarFundo = async (estilo: EstiloDeFundo, imagem?: string | null) => {
    const novaImagem = imagem === undefined ? imagemFundo : imagem
    setEstiloFundo(estilo)
    if (imagem !== undefined) setImagemFundo(imagem)
    guardarEstiloDeFundo(estilo)
    await sincronizarProcessamentoDaCamera(estilo, novaImagem, aparencia)
    await trocarVideoNaConexao()
  }

  /**
   * Mexe nos ajustes de aparência ao vivo (SPEC-20260918-0040): arrastar o
   * controle muda a imagem na hora, sem recriar o vídeo.
   *
   * A faixa que sai só é trocada quando o processamento LIGA ou DESLIGA — é o
   * que acontece ao sair da aparência neutra com o fundo desligado, e ao voltar
   * a ela. Ajuste de grau não troca faixa nenhuma.
   */
  const trocarAparencia = async (ajustesDeAparencia: Aparencia) => {
    setAparencia(ajustesDeAparencia)
    guardarAparencia(ajustesDeAparencia)
    const tinhaProcessamento = fundoRef.current !== null
    await sincronizarProcessamentoDaCamera(estiloFundo, imagemFundo, ajustesDeAparencia)
    if (tinhaProcessamento !== (fundoRef.current !== null)) await trocarVideoNaConexao()
  }

  /** Câmera de verdade desligada (a luz apaga), não só escondida. */
  const alternarCam = async () => {
    if (cameraRef.current) {
      pararFundo()
      cameraRef.current.stop()
      cameraRef.current = null
      setCamLigada(false)
      await trocarVideoNaConexao()
      return
    }
    try {
      if (!(await garantirPermissao('camera'))) return
      const midia = await navigator.mediaDevices.getUserMedia({ video: restricaoDeVideo() })
      cameraRef.current = midia.getVideoTracks()[0] ?? null
      setCamLigada(cameraRef.current !== null)
      await ligarFundoSePreciso()
      await trocarVideoNaConexao()
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível ligar a câmera. Confira a permissão e se outro app está usando.' })
    }
  }

  /** Para de compartilhar — a própria pessoa, ou o "Parar" da barra do navegador. */
  const pararCompartilharTela = () => {
    if (!telaRef.current) return
    telaRef.current.stop()
    telaRef.current = null
    telaAudioRef.current?.stop()
    telaAudioRef.current = null
    setCompartilhando(false)
    setTelaComAudio(false)
    setSomDaTela(true)
    void trocarVideoNaConexao()
    void trocarAudioNaConexao()
  }

  /**
   * Compartilhar a tela. Pede áudio sempre — é o que faz o PRÓPRIO seletor do
   * navegador mostrar o interruptor "Compartilhar áudio do sistema"; um menu
   * nosso perguntando a mesma coisa ANTES ficava redundante e confuso (pedido
   * 2026-09-14, duas telas pedindo a mesma escolha). Nem toda fonte permite —
   * Chrome só captura áudio de uma GUIA ou da TELA INTEIRA, nunca de uma
   * janela avulsa — aí a tela compartilha só a imagem, em silêncio.
   */
  const compartilharTela = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.push({ tone: 'danger', message: 'Este aparelho não permite compartilhar a tela.' })
      return
    }
    try {
      const midia = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true })
      const faixa = midia.getVideoTracks()[0] ?? null
      if (!faixa) return
      // Texto nítido vale mais que movimento suave numa tela de trabalho.
      faixa.contentHint = 'detail'
      // "Parar de compartilhar" da barra do próprio navegador também encerra.
      faixa.onended = () => {
        if (telaRef.current === faixa) pararCompartilharTela()
      }
      const faixaDeAudio = midia.getAudioTracks()[0] ?? null
      telaRef.current = faixa
      telaAudioRef.current = faixaDeAudio
      setCompartilhando(true)
      setTelaComAudio(faixaDeAudio !== null)
      setSomDaTela(true)
      await trocarVideoNaConexao()
      await trocarAudioNaConexao()
    } catch {
      // A pessoa fechou o seletor de tela: não é erro, não avisa nada.
    }
  }

  /** Silencia/reativa só o som da tela, sem parar de compartilhar a imagem. */
  const alternarSomDaTela = () => {
    if (!telaAudioRef.current) return
    const ligado = !somDaTela
    telaAudioRef.current.enabled = ligado
    setSomDaTela(ligado)
  }

  /* --------------------------------------------- sinais que chegam --- */

  useEffect(() => {
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo === 'sala') {
        void tratarEventoDaSala(evento)
        return
      }
      if (evento.tipo !== 'chamada' || evento.sessao === SESSAO) return
      void tratarSinal(evento)
    })
    // Os handlers leem tudo por ref; reinscrever a cada render perderia sinais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * O que acontece na sala: gente entrando, saindo, sinais de mídia e — só para
   * quem abriu — quem está batendo na porta.
   */
  const tratarEventoDaSala = async (evento: AvisoDaSala) => {
    // Outras salas (as do Escritório) têm a tela delas: aqui só a sala desta chamada.
    if (evento.evento.nome !== 'portaria' && evento.roomId !== salaRef.current?.roomId) return
    if (evento.evento.nome === 'portaria') {
      const { guestId, visitante, recado } = evento.evento
      setNaPorta((atual) => (atual.some((p) => p.guestId === guestId) ? atual : [...atual, { guestId, visitante, recado }]))
      // Fora da tela da chamada a decisão continua chegando: o Windows avisa.
      notificarNoSistema({
        titulo: `${visitante} quer entrar na chamada`,
        corpo: recado ?? 'Abra o Orbind para deixar entrar.',
        grupo: `portaria-${guestId}`,
        aoClicar: () => window.focus(),
        sempre: true,
      })
      return
    }
    if (evento.evento.nome === 'encerrada') {
      encerrarSala(false)
      return
    }
    await malha().tratarEvento(evento.evento)
    // Quem acabou de entrar precisa saber o que já estou mandando.
    if (evento.evento.nome === 'entrou') {
      malha().anunciarMidia(cameraRef.current !== null, telaRef.current !== null)
    }
  }

  const tratarSinal = async (evento: AvisoChamada) => {
    const { sinal } = evento
    const atual = chamadaRef.current

    if (sinal.acao === 'convite') {
      if (atual) {
        canalDePresenca.enviarSinalDeChamada({
          callId: evento.callId,
          channelId: evento.channelId,
          para: evento.de,
          sessao: SESSAO,
          sinal: { acao: 'ocupado' },
        })
        return
      }
      atualizar({
        fase: 'tocando',
        callId: evento.callId,
        channelId: evento.channelId,
        outro: { memberId: evento.de, nome: evento.deNome, foto: null },
        papel: 'atende',
        video: sinal.video !== false,
      })
      pararSom.current = tocarSom('toque')
      // O Windows avisa que o telefone está tocando — SEMPRE, mesmo com o
      // Orbind em foco (SPEC-20261104-1141): quem está em outra tela ou olhando
      // outra coisa precisa ver a chamada na bandeja.
      notificarNoSistema({
        titulo: `${sinal.video !== false ? 'Videochamada' : 'Chamada de voz'} de ${evento.deNome}`,
        corpo: 'Abra o Orbind para atender.',
        grupo: `chamada-${evento.callId}`,
        aoClicar: () => window.focus(),
        sempre: true,
      })
      // Quem liga cancela sozinho no prazo; a folga cobre o aviso que se perde.
      timer.current = setTimeout(() => {
        if (chamadaRef.current?.callId === evento.callId && chamadaRef.current.fase === 'tocando') {
          limpar(`Chamada perdida de ${evento.deNome}.`)
        }
      }, CHAMADA_TOCA_POR_MS + 5_000)
      return
    }

    if (!atual || atual.callId !== evento.callId) return
    const nome = atual.outro.nome

    switch (sinal.acao) {
      case 'aceitar': {
        // Minha OUTRA janela atendeu: esta só para de tocar.
        if (atual.papel === 'atende') {
          if (atual.fase === 'tocando') limpar()
          return
        }
        if (atual.fase !== 'chamando') return
        silenciar()
        const conectando: Chamada = { ...atual, fase: 'conectando' }
        atualizar(conectando)
        const pc = criarConexao(conectando)
        const oferta = await pc.createOffer()
        await pc.setLocalDescription(oferta)
        enviar(conectando, { acao: 'oferta', sdp: oferta.sdp ?? '' })
        prazoParaConectar()
        return
      }
      case 'recusar':
        if (atual.papel === 'atende') {
          if (atual.fase === 'tocando') limpar()
          return
        }
        limpar(`${nome} recusou a chamada.`)
        return
      case 'ocupado':
        limpar(`${nome} está em outra chamada.`)
        return
      case 'indisponivel':
        limpar(`${nome} não está conectado ao Orbind agora.`)
        return
      case 'cancelar':
        limpar(atual.fase === 'tocando' ? `Chamada perdida de ${nome}.` : undefined)
        return
      case 'encerrar':
        limpar('Chamada encerrada.')
        return
      case 'midia':
        setOutroMidia({ video: sinal.video, tela: sinal.tela })
        return
      case 'sala':
        // Quem atendeu entra na MESMA sala de quem ligou — é o que faz o
        // convidado do link aparecer para os dois (SPEC-20260917-0047).
        if (atual.papel === 'atende' && !salaRef.current) {
          guardarSala({ roomId: sinal.roomId, inviteToken: '' })
          entrarNaSalaDaChamada(sinal.roomId)
        }
        return
      case 'oferta': {
        const pc = pcRef.current
        if (atual.papel !== 'atende' || !pc) return
        await pc.setRemoteDescription({ type: 'offer', sdp: sinal.sdp })
        // Os canais vieram na oferta: agora preenche com o microfone e a câmera.
        await ligarFaixasNaConexao(pc)
        await aplicarIcePendente(pc)
        const resposta = await pc.createAnswer()
        await pc.setLocalDescription(resposta)
        enviar(atual, { acao: 'resposta', sdp: resposta.sdp ?? '' })
        return
      }
      case 'resposta': {
        const pc = pcRef.current
        if (atual.papel !== 'liga' || !pc) return
        await pc.setRemoteDescription({ type: 'answer', sdp: sinal.sdp })
        await aplicarIcePendente(pc)
        return
      }
      case 'ice': {
        const pc = pcRef.current
        if (pc?.remoteDescription) await pc.addIceCandidate(sinal.candidato).catch(() => undefined)
        else icePendente.current.push(sinal.candidato)
        return
      }
    }
  }

  // Fechar a janela no meio da chamada avisa o outro lado.
  useEffect(() => {
    const aoSair = () => {
      const atual = chamadaRef.current
      if (!atual || atual.fase === 'antessala') return
      enviar(atual, { acao: atual.fase === 'chamando' ? 'cancelar' : atual.fase === 'tocando' ? 'recusar' : 'encerrar' })
    }
    window.addEventListener('beforeunload', aoSair)
    return () => window.removeEventListener('beforeunload', aoSair)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tipoDaChamada = chamada?.video ? 'Videochamada' : 'Chamada de voz'

  return (
    <Contexto.Provider value={{ ocupado: chamada !== null, ligar }}>
      {children}
      {chamada
        ? createPortal(
            chamada.fase === 'antessala' ? (
              <Antessala
                outro={chamada.outro}
                video={chamada.video}
                local={local}
                faixaDoMicrofone={microfoneRef.current}
                micLigado={micLigado}
                camLigada={camLigada}
                estiloFundo={estiloFundo}
                imagemFundo={imagemFundo}
                aparencia={aparencia}
                onMic={alternarMic}
                onCam={() => void alternarCam()}
                onTrocarMicrofone={(id) => void trocarMicrofone(id)}
                onTrocarCamera={(id) => void trocarCamera(id)}
                onTrocarFundo={(estilo, imagem) => void trocarFundo(estilo, imagem)}
                onTrocarAparencia={(nova) => void trocarAparencia(nova)}
                onChamar={chamarAgora}
                onCancelar={desligar}
                linkDoConvite={sala ? `${window.location.origin}/entrar/${sala.inviteToken}` : null}
                naPorta={naPorta}
                convidados={convidados}
                onDecidir={decidirNaPorta}
              />
            ) : chamada.fase === 'tocando' ? (
              <div className="vc-toque" role="alertdialog" aria-label={`${tipoDaChamada} de ${chamada.outro.nome}`}>
                <span className="vc-toque__avatar" aria-hidden>
                  {iniciais(chamada.outro.nome)}
                </span>
                <span className="vc-toque__texto">
                  <span className="vc-toque__nome">{chamada.outro.nome}</span>
                  <span className="vc-toque__sub">
                    <i className={chamada.video ? 'fas fa-video' : 'fas fa-phone-alt'} aria-hidden /> {tipoDaChamada}{' '}
                    recebida…
                  </span>
                </span>
                <span className="vc-toque__acoes">
                  <button type="button" className="vc-botao vc-botao--recusar" onClick={recusar} aria-label="Recusar">
                    <i className="fas fa-phone-slash" aria-hidden />
                    <span>Recusar</span>
                  </button>
                  {/* Atender com voz sempre existe; com vídeo, só se o convite for de vídeo. */}
                  <button
                    type="button"
                    className="vc-botao vc-botao--aceitar"
                    onClick={() => void aceitar(false)}
                    aria-label="Atender com voz"
                    title="Atender sem câmera"
                  >
                    <i className="fas fa-phone-alt" aria-hidden />
                    <span>Voz</span>
                  </button>
                  {chamada.video ? (
                    <button
                      type="button"
                      className="vc-botao vc-botao--aceitar"
                      onClick={() => void aceitar(true)}
                      aria-label="Atender com vídeo"
                    >
                      <i className="fas fa-video" aria-hidden />
                      <span>Vídeo</span>
                    </button>
                  ) : null}
                </span>
              </div>
            ) : (
              <TelaDaChamada
                chamada={chamada}
                local={local}
                localTela={localTela}
                remoto={remoto}
                remotoTela={remotoTela}
                outroMidia={outroMidia}
                inicio={inicio}
                micLigado={micLigado}
                camLigada={camLigada}
                compartilhando={compartilhando}
                telaComAudio={telaComAudio}
                somDaTela={somDaTela}
                onMic={alternarMic}
                onCam={() => void alternarCam()}
                onCompartilhar={() => void compartilharTela()}
                onPararTela={pararCompartilharTela}
                onAlternarSomDaTela={alternarSomDaTela}
                onTrocarMicrofone={(id) => void trocarMicrofone(id)}
                onTrocarCamera={(id) => void trocarCamera(id)}
                estiloFundo={estiloFundo}
                imagemFundo={imagemFundo}
                onTrocarFundo={(estilo, imagem) => void trocarFundo(estilo, imagem)}
                onDesligar={desligar}
                naPorta={naPorta}
                convidados={convidados}
                onDecidir={decidirNaPorta}
              />
            ),
            document.body,
          )
        : null}
    </Contexto.Provider>
  )
}

function TelaDaChamada({
  chamada,
  local,
  localTela,
  remoto,
  remotoTela,
  outroMidia,
  inicio,
  micLigado,
  camLigada,
  compartilhando,
  telaComAudio,
  somDaTela,
  onMic,
  onCam,
  onCompartilhar,
  onPararTela,
  onAlternarSomDaTela,
  onTrocarMicrofone,
  onTrocarCamera,
  estiloFundo,
  imagemFundo,
  onTrocarFundo,
  onDesligar,
  naPorta,
  convidados,
  onDecidir,
}: {
  chamada: Chamada
  local: MediaStream | null
  localTela: MediaStream | null
  remoto: MediaStream | null
  remotoTela: MediaStream | null
  outroMidia: { video: boolean; tela: boolean }
  inicio: number | null
  micLigado: boolean
  camLigada: boolean
  compartilhando: boolean
  telaComAudio: boolean
  somDaTela: boolean
  onMic: () => void
  onCam: () => void
  onCompartilhar: () => void
  onPararTela: () => void
  onAlternarSomDaTela: () => void
  onTrocarMicrofone: (id: string) => void
  onTrocarCamera: (id: string) => void
  estiloFundo: EstiloDeFundo
  imagemFundo: string | null
  onTrocarFundo: (estilo: EstiloDeFundo, imagem?: string | null) => void
  onDesligar: () => void
  /** Quem bateu na porta e espera decisão (só o dono da sala recebe). */
  naPorta: PedidoNaPorta[]
  /** Quem entrou pelo link e já está na chamada. */
  convidados: ParticipanteAoVivo[]
  onDecidir: (guestId: string, aprovar: boolean) => void
}) {
  const videoRemoto = useRef<HTMLVideoElement>(null)
  const videoTelaRemota = useRef<HTMLVideoElement>(null)
  const videoLocal = useRef<HTMLVideoElement>(null)
  const videoLocalTela = useRef<HTMLVideoElement>(null)
  const [agora, setAgora] = useState(Date.now())
  const [menuVolume, setMenuVolume] = useState<HTMLElement | null>(null)
  /**
   * MINIMIZAR (SPEC-20260916-2346): a chamada encolhe para um retângulo no
   * canto e o resto do Orbind volta a ser clicável — dá para procurar um item,
   * abrir um quadro e continuar falando. O `<video>` NUNCA é desmontado: a
   * conexão e o som seguem intactos, só o tamanho muda.
   */
  const [minimizada, setMinimizada] = useState(false)
  const [menuAparelhos, setMenuAparelhos] = useState<HTMLElement | null>(null)
  const [menuFundo, setMenuFundo] = useState<HTMLElement | null>(null)
  // Volume de quem ESTOU OUVINDO — o outro lado, voz e som da tela dele
  // juntos (pedido 2026-09-14: "a pessoa que está olhando tem que poder
  // aumentar e diminuir"). É só PLAYBACK local: não avisa ninguém, não muda
  // nada na conexão — só o quanto sai da CAIXA DE SOM daqui.
  const [volume, setVolume] = useState(1)
  const volumeAntes = useRef(1)

  useEffect(() => {
    if (!menuVolume) return
    const aoClicarFora = (e: MouseEvent) => {
      const alvo = e.target as HTMLElement | null
      if (!alvo || alvo === menuVolume || menuVolume.contains(alvo) || alvo.closest('.vc-volume')) return
      setMenuVolume(null)
    }
    window.addEventListener('mousedown', aoClicarFora, true)
    return () => window.removeEventListener('mousedown', aoClicarFora, true)
  }, [menuVolume])

  useEffect(() => {
    if (videoRemoto.current) videoRemoto.current.srcObject = remoto
    // manda o som para a caixa escolhida (SPEC-20260916-2346)
    void aplicarSaida(videoRemoto.current)
  }, [remoto])
  useEffect(() => {
    if (videoRemoto.current) videoRemoto.current.volume = volume
  }, [volume, remoto])
  useEffect(() => {
    if (videoLocal.current) videoLocal.current.srcObject = local
  }, [local])
  useEffect(() => {
    if (videoLocalTela.current) videoLocalTela.current.srcObject = localTela
  }, [localTela])
  useEffect(() => {
    if (videoTelaRemota.current) videoTelaRemota.current.srcObject = remotoTela
  }, [remotoTela])
  useEffect(() => {
    if (inicio === null) return
    const relogio = setInterval(() => setAgora(Date.now()), 1_000)
    return () => clearInterval(relogio)
  }, [inicio])

  // O canal de vídeo existe desde o início; o que diz se há imagem é o aviso
  // de mídia do outro lado (câmera ou tela ligada).
  const outroMostra = outroMidia.video || outroMidia.tela
  const outroMostraCamera = outroMidia.video
  const temTelaRemota = outroMidia.tela && remotoTela !== null
  const status =
    chamada.fase === 'chamando'
      ? 'Chamando…'
      : chamada.fase === 'conectando'
      ? 'Conectando…'
      : duracao(inicio === null ? 0 : agora - inicio)

  return (
    <div
      className={`vc-tela${minimizada ? ' vc-tela--mini' : ''}`}
      role="dialog"
      aria-label={`Chamada com ${chamada.outro.nome}`}
      // minimizada, o retângulo inteiro volta para a tela cheia (menos os botões)
      onClick={minimizada ? (e) => {
        if ((e.target as HTMLElement).closest('button')) return
        setMinimizada(false)
      } : undefined}
    >
      {/*
        A TELA compartilhada pelo outro lado vem em canal próprio
        (SPEC-20260917-0004): ela fica em destaque e a CÂMERA da pessoa vira
        miniatura — as duas ao mesmo tempo, como no Meet. Sem tela, a câmera é
        o destaque. O <video> da câmera NUNCA sai da árvore: é ele que toca a
        VOZ do outro lado.
      */}
      <video
        ref={videoTelaRemota}
        className="vc-tela__remoto vc-tela__remoto--tela"
        autoPlay
        playsInline
        hidden={!temTelaRemota}
      />
      <video
        ref={videoRemoto}
        className={temTelaRemota ? 'vc-tela__tira vc-tela__tira--pessoa' : 'vc-tela__remoto'}
        autoPlay
        playsInline
        style={outroMostraCamera || temTelaRemota ? undefined : { opacity: 0 }}
      />
      {!outroMostra ? (
        <div className="vc-tela__espera">
          <span className="vc-tela__avatar" aria-hidden>
            {iniciais(chamada.outro.nome)}
          </span>
          {chamada.fase === 'em-chamada' ? <span className="vc-tela__semvideo">Câmera desligada</span> : null}
        </div>
      ) : null}
      {temTelaRemota && !outroMostraCamera ? (
        <span className="vc-tela__tira vc-tela__tira--pessoa vc-tela__tira--vazia" aria-hidden>
          {iniciais(chamada.outro.nome)}
        </span>
      ) : null}

      <div className="vc-tela__topo">
        <span className="vc-tela__nome">{chamada.outro.nome}</span>
        <span className="vc-tela__status" aria-live="polite">
          {outroMidia.tela ? `Compartilhando a tela · ${status}` : status}
        </span>
      </div>

      {/* Minimizar: a chamada vira um retângulo no canto e o Orbind fica
          navegável (SPEC-20260916-2346). Na mini, o mesmo botão restaura. */}
      <button
        type="button"
        className={`vc-tela__mini${minimizada ? ' vc-tela__mini--restaurar' : ''}`}
        aria-label={minimizada ? 'Voltar a chamada para a tela toda' : 'Minimizar a chamada e continuar navegando'}
        title={minimizada ? 'Tela toda' : 'Minimizar'}
        onClick={() => setMinimizada((v) => !v)}
      >
        <i className={minimizada ? 'fas fa-expand' : 'fas fa-compress'} aria-hidden />
      </button>

      {compartilhando ? (
        <div className="vc-tela__aviso" role="status">
          <i className="fas fa-desktop" aria-hidden /> Compartilhando a tela
          {telaComAudio ? (somDaTela ? ' · com som' : ' · som silenciado') : ''}
        </div>
      ) : null}

      {/* Quem veio pelo link e já foi aprovado aparece junto dos demais. */}
      {convidados.length > 0 ? (
        <div className="vc-tela__convidados">
          {convidados.map((p) => (
            <VideoDoConvidado key={p.id} participante={p} />
          ))}
        </div>
      ) : null}

      {/* PORTARIA: quem veio pelo link espera aqui a decisão do dono. */}
      <Portaria pedidos={naPorta} onDecidir={onDecidir} />

      {/* Minha câmera e minha tela em miniaturas separadas — antes uma
          escondia a outra (SPEC-20260917-0004). */}
      <video ref={videoLocal} className="vc-tela__local" autoPlay playsInline muted hidden={!camLigada} />
      <video
        ref={videoLocalTela}
        className={`vc-tela__local vc-tela__local--tela${camLigada ? ' vc-tela__local--segunda' : ''}`}
        autoPlay
        playsInline
        muted
        hidden={!compartilhando}
      />

      <div className="vc-tela__controles">
        {/* Volume de quem EU ESTOU OUVINDO — só ajusta o playback aqui, não
            avisa nem muda nada do outro lado. */}
        <button
          type="button"
          className="vc-botao vc-botao--redondo"
          aria-haspopup="dialog"
          aria-expanded={menuVolume !== null}
          aria-label={volume === 0 ? 'Som mudo — ajustar volume' : `Volume ${Math.round(volume * 100)}% — ajustar`}
          title="Volume"
          onClick={(e) => {
            // `e.currentTarget` só vale DURANTE o despacho do evento — lido
            // direto dentro do updater do setState (chamado depois) ele já
            // vem nulo. Por isso a captura numa variável comum primeiro.
            const alvo = e.currentTarget
            setMenuVolume((atual) => (atual ? null : alvo))
          }}
        >
          <i className={iconeDeVolume(volume)} aria-hidden />
        </button>
        {menuVolume ? (
          <AnchoredOverlay
            anchor={menuVolume}
            onClose={() => setMenuVolume(null)}
            prefer="direita"
            gap={14}
            role="dialog"
            aria-label="Volume"
            className="vc-volume"
          zIndex={CAMADA_DA_CHAMADA}
          >
            <button
              type="button"
              className="vc-volume__mudo"
              aria-label={volume === 0 ? 'Reativar o som' : 'Mudo'}
              title={volume === 0 ? 'Reativar o som' : 'Mudo'}
              onClick={() => {
                if (volume === 0) {
                  setVolume(volumeAntes.current || 1)
                } else {
                  volumeAntes.current = volume
                  setVolume(0)
                }
              }}
            >
              <i className={iconeDeVolume(volume)} aria-hidden />
            </button>
            <input
              type="range"
              className="vc-volume__regua"
              min={0}
              max={100}
              value={Math.round(volume * 100)}
              aria-label="Volume de quem você está ouvindo"
              onChange={(e) => setVolume(Number(e.target.value) / 100)}
            />
            <span className="vc-volume__valor">{Math.round(volume * 100)}%</span>
          </AnchoredOverlay>
        ) : null}
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${micLigado ? '' : ' vc-botao--desligado'}`}
          aria-pressed={!micLigado}
          aria-label={micLigado ? 'Desligar microfone' : 'Ligar microfone'}
          title={micLigado ? 'Desligar microfone' : 'Ligar microfone'}
          onClick={onMic}
        >
          <i className={micLigado ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden />
        </button>
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${camLigada ? '' : ' vc-botao--desligado'}`}
          aria-pressed={camLigada}
          aria-label={camLigada ? 'Desligar câmera' : 'Ligar câmera'}
          title={camLigada ? 'Desligar câmera' : 'Ligar câmera'}
          onClick={onCam}
        >
          <i className={camLigada ? 'fas fa-video' : 'fas fa-video-slash'} aria-hidden />
        </button>
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${compartilhando ? ' vc-botao--ativo' : ''}`}
          aria-pressed={compartilhando}
          aria-label={compartilhando ? 'Parar de compartilhar a tela' : 'Compartilhar a tela'}
          title={compartilhando ? 'Parar de compartilhar a tela' : 'Compartilhar a tela'}
          disabled={chamada.fase !== 'em-chamada'}
          onClick={compartilhando ? onPararTela : onCompartilhar}
        >
          <i className="fas fa-desktop" aria-hidden />
        </button>
        {/*
          Silenciar só o som DA TELA que EU compartilho, sem parar de
          compartilhar a imagem — o único lugar para com/sem som agora (o
          navegador já pergunta na hora de escolher a tela/janela; um menu
          nosso ANTES disso era pergunta em dobro, pedido 2026-09-14).
        */}
        {compartilhando && telaComAudio ? (
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${somDaTela ? ' vc-botao--ativo' : ''}`}
            aria-pressed={somDaTela}
            aria-label={somDaTela ? 'Silenciar o som da tela' : 'Ativar o som da tela'}
            title={somDaTela ? 'Silenciar o som da tela' : 'Ativar o som da tela'}
            onClick={onAlternarSomDaTela}
          >
            <i className={somDaTela ? 'fas fa-volume-up' : 'fas fa-volume-mute'} aria-hidden />
          </button>
        ) : null}
        {/* Escolher microfone, câmera e caixa de som SEM sair da chamada
            (SPEC-20260916-2346). A troca é na hora: a faixa da conexão é
            substituída, ninguém cai. */}
        <button
          type="button"
          className="vc-botao vc-botao--redondo"
          aria-haspopup="dialog"
          aria-expanded={menuAparelhos !== null}
          aria-label="Escolher microfone, câmera e som"
          title="Aparelhos"
          onClick={(e) => {
            const alvo = e.currentTarget
            setMenuAparelhos((atual) => (atual ? null : alvo))
          }}
        >
          <i className="fas fa-sliders-h" aria-hidden />
        </button>
        {menuAparelhos ? (
          <MenuDeAparelhos
            ancora={menuAparelhos}
            onFechar={() => setMenuAparelhos(null)}
            onTrocarMicrofone={onTrocarMicrofone}
            onTrocarCamera={onTrocarCamera}
            onTrocarSaida={() => void aplicarSaida(videoRemoto.current)}
          />
        ) : null}
        {/* FUNDO da câmera (SPEC-20260916-2346): desfoque ou imagem sua. */}
        <button
          type="button"
          className={`vc-botao vc-botao--redondo${estiloFundo !== 'nenhum' ? ' vc-botao--ativo' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={menuFundo !== null}
          aria-label="Fundo da câmera"
          title="Fundo"
          disabled={!camLigada}
          onClick={(e) => {
            const alvo = e.currentTarget
            setMenuFundo((atual) => (atual ? null : alvo))
          }}
        >
          <i className="fas fa-image" aria-hidden />
        </button>
        {menuFundo ? (
          <MenuDeFundo
            ancora={menuFundo}
            estilo={estiloFundo}
            imagem={imagemFundo}
            onFechar={() => setMenuFundo(null)}
            onTrocar={onTrocarFundo}
          />
        ) : null}
        <button
          type="button"
          className="vc-botao vc-botao--redondo vc-botao--recusar"
          aria-label="Desligar"
          title="Desligar"
          onClick={onDesligar}
        >
          <i className="fas fa-phone-slash" aria-hidden />
        </button>
      </div>
    </div>
  )
}

function novoId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random())
}

/** Mudo, baixo ou alto — o mesmo ícone do botão do controle de volume. */
function iconeDeVolume(volume: number): string {
  if (volume === 0) return 'fas fa-volume-mute'
  if (volume < 0.5) return 'fas fa-volume-down'
  return 'fas fa-volume-up'
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}

function duracao(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Som gerado na hora (sem arquivo de áudio): `toque` é o telefone tocando para
 * quem recebe; `chamando` é o sinal baixo que quem liga ouve enquanto espera.
 * O navegador pode bloquear som sem um clique antes — aí só o aviso visual.
 */
function tocarSom(tipo: 'toque' | 'chamando'): () => void {
  let contexto: AudioContext
  try {
    contexto = new AudioContext()
  } catch {
    return () => undefined
  }
  void contexto.resume().catch(() => undefined)

  const bip = (inicio: number, frequencias: number[], duracaoS: number, volume: number) => {
    for (const frequencia of frequencias) {
      const oscilador = contexto.createOscillator()
      const ganho = contexto.createGain()
      oscilador.frequency.value = frequencia
      ganho.gain.setValueAtTime(0, inicio)
      ganho.gain.linearRampToValueAtTime(volume, inicio + 0.03)
      ganho.gain.setValueAtTime(volume, inicio + duracaoS - 0.05)
      ganho.gain.linearRampToValueAtTime(0, inicio + duracaoS)
      oscilador.connect(ganho).connect(contexto.destination)
      oscilador.start(inicio)
      oscilador.stop(inicio + duracaoS + 0.05)
    }
  }

  const ciclo = () => {
    const t = contexto.currentTime + 0.05
    if (tipo === 'toque') {
      bip(t, [480, 620], 0.4, 0.07)
      bip(t + 0.6, [480, 620], 0.4, 0.07)
    } else {
      bip(t, [440, 480], 1.0, 0.035)
    }
  }
  ciclo()
  const intervalo = setInterval(ciclo, tipo === 'toque' ? 2_200 : 3_000)
  return () => {
    clearInterval(intervalo)
    void contexto.close().catch(() => undefined)
  }
}
